import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { AvailabilityRule, BookingDto, BookingSettings, SlotsResponse } from '../../../../shared/types';
import type { Ctx } from '../ctx';
import { Icon } from '../icons';
import { dayLabel, fmtTime, localDay, minuteToTime, thisMonth, timeToMinute, todayKey } from '../format';
import { Calendar, Empty, ErrorBanner, Field, Panel } from '../ui';
import { TimeInput } from '../pickers';
import { ask } from '../dialogs';

function monthDays(month: string): { from: string; to: string } {
  const [y, m] = month.split('-').map(Number);
  const last = new Date(y, m, 0).getDate();
  const p = (n: number) => String(n).padStart(2, '0');
  return { from: `${y}-${p(m)}-01`, to: `${y}-${p(m)}-${p(last)}` };
}

const WEEK = [
  { d: 1, label: 'Lunedì' },
  { d: 2, label: 'Martedì' },
  { d: 3, label: 'Mercoledì' },
  { d: 4, label: 'Giovedì' },
  { d: 5, label: 'Venerdì' },
  { d: 6, label: 'Sabato' },
  { d: 0, label: 'Domenica' },
];

/** Prenotazione degli slot con il consulente. Clienti e commerciali prenotano; il consulente imposta le disponibilità. */
export function BookingPage({ ctx }: { ctx: Ctx }) {
  const isAdmin = ctx.me.user.role === 'admin';
  const [tab, setTab] = useState<'list' | 'settings'>('list');

  if (!isAdmin) return <Booker ctx={ctx} />;
  return (
    <div className="panels single" style={{ gridTemplateRows: 'auto minmax(0, 1fr)' }}>
      <div className="quote-tabs" style={{ marginBottom: 0 }}>
        <button type="button" className={`quote-tab${tab === 'list' ? ' active' : ''}`} title="Chi ha prenotato un appuntamento con te" onClick={() => setTab('list')}>
          Prenotazioni
        </button>
        <button type="button" className={`quote-tab${tab === 'settings' ? ' active' : ''}`} title="I giorni e gli orari in cui si può prenotare" onClick={() => setTab('settings')}>
          Disponibilità
        </button>
      </div>
      {tab === 'list' ? <AdminList ctx={ctx} /> : <Settings ctx={ctx} />}
    </div>
  );
}

function slotLine(b: BookingDto): string {
  return `${dayLabel(localDay(b.startsAt))} · ${fmtTime(b.startsAt)}–${fmtTime(b.endsAt)}`;
}

// ---------------------------------------------------------------------------

function Booker({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const [month, setMonth] = useState(thisMonth());
  const [selectedDay, setSelectedDay] = useState(todayKey());
  const [data, setData] = useState<SlotsResponse | null>(null);
  const [mine, setMine] = useState<BookingDto[]>([]);
  const [picked, setPicked] = useState<string[]>([]);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const r = monthDays(month);
      setData(await api.get<SlotsResponse>(`/api/booking/slots?from=${r.from}&to=${r.to}`));
      setMine(await api.get<BookingDto[]>('/api/booking/mine'));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api, month]);

  useEffect(() => {
    void load();
  }, [load]);

  // un nuovo evento di prenotazione (es. annullata dal consulente) aggiorna l'elenco
  useEffect(() => {
    if (ctx.event?.type === 'booking.updated') void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ctx.eventSeq]);

  const marks = useMemo(() => Object.fromEntries((data?.days ?? []).map((d) => [d.date, true])), [data]);
  const daySlots = data?.days.find((d) => d.date === selectedDay)?.slots ?? [];

  const toggle = (iso: string) => setPicked((p) => (p.includes(iso) ? p.filter((x) => x !== iso) : [...p, iso].sort()));

  const book = async () => {
    setBusy(true);
    setError(null);
    try {
      const r = await api.post<BookingDto[]>('/api/booking', { slots: picked, note: note.trim() || null });
      setDone(r.length === 1 ? 'Prenotazione confermata.' : `${r.length} prenotazioni confermate.`);
      setPicked([]);
      setNote('');
      await load();
    } catch (e) {
      setError((e as Error).message);
      setPicked([]);
      void load();
    } finally {
      setBusy(false);
    }
  };

  const cancel = async (b: BookingDto) => {
    if (!(await ask(`Annullare ${slotLine(b)}?`))) return;
    try {
      await api.del(`/api/booking/${b.id}`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <div className="panels">
      <Panel title="Prenota con il consulente" subtitle={data ? `Appuntamenti da ${data.slotMin} minuti: puoi sceglierne più di uno` : undefined}>
        <ErrorBanner message={error} onClose={() => setError(null)} />
        {done && (
          <div className="note good" style={{ marginBottom: 12 }} onClick={() => setDone(null)}>
            {done}
          </div>
        )}
        <Calendar month={month} selected={selectedDay} marks={marks} onSelect={setSelectedDay} onMonth={setMonth} />
        <h3 style={{ margin: '18px 0 8px' }}>{dayLabel(selectedDay)}</h3>
        {!data ? (
          <div className="muted small">Caricamento…</div>
        ) : daySlots.length === 0 ? (
          <div className="muted small">Nessun orario libero: scegli un giorno evidenziato.</div>
        ) : (
          <div className="bk-slots">
            {daySlots.map((s) => (
              <button key={s} type="button" className={`bk-slot${picked.includes(s) ? ' on' : ''}`} onClick={() => toggle(s)}>
                {fmtTime(s)}
              </button>
            ))}
          </div>
        )}

        {picked.length > 0 && (
          <div className="bk-cart">
            <b>
              {picked.length} {picked.length === 1 ? 'orario scelto' : 'orari scelti'}
            </b>
            <ul>
              {picked.map((s) => (
                <li key={s}>
                  {dayLabel(localDay(s))} · {fmtTime(s)}{' '}
                  <button type="button" className="btn ghost" onClick={() => toggle(s)} title="Togli questo orario">
                    <Icon name="x" size={12} />
                  </button>
                </li>
              ))}
            </ul>
            <Field label="Argomento (facoltativo)">
              <input value={note} onChange={(e) => setNote(e.target.value)} maxLength={500} placeholder="Di cosa vuoi parlare?" />
            </Field>
            <button type="button" className="btn dark" onClick={() => void book()} disabled={busy}>
              {busy ? 'Prenotazione…' : picked.length === 1 ? 'Prenota' : `Prenota ${picked.length} orari`}
            </button>
          </div>
        )}
      </Panel>

      <Panel title="Le mie prenotazioni" subtitle="Annullabili fino a 2 ore prima">
        {mine.length === 0 ? (
          <Empty icon="calendar">Nessuna prenotazione in programma.</Empty>
        ) : (
          <div className="ag-list">
            {mine.map((b) => (
              <div key={b.id} className="ag-row" style={{ cursor: 'default' }}>
                <span className="ag-title">
                  {slotLine(b)}
                  {b.note && <span className="muted"> · {b.note}</span>}
                </span>
                <button type="button" className="btn ghost" onClick={() => void cancel(b)} title="Annulla la prenotazione">
                  <Icon name="trash" size={14} />
                </button>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

// ---------------------------------------------------------------------------

function AdminList({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const [list, setList] = useState<BookingDto[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setList(await api.get<BookingDto[]>('/api/booking/all'));
    } catch (e) {
      setError((e as Error).message);
    }
  }, [api]);

  useEffect(() => {
    void load();
  }, [load, ctx.eventSeq]);

  const cancel = async (b: BookingDto) => {
    if (!(await ask(`Annullare la prenotazione di ${b.userName} (${slotLine(b)})?`))) return;
    try {
      await api.del(`/api/booking/${b.id}`);
      await load();
    } catch (e) {
      setError((e as Error).message);
    }
  };

  return (
    <Panel title="Prenotazioni" subtitle="Chi ha prenotato un appuntamento con te">
      <ErrorBanner message={error} onClose={() => setError(null)} />
      {!list ? (
        <div className="muted small">Caricamento…</div>
      ) : list.length === 0 ? (
        <Empty icon="calendar">Nessuna prenotazione in programma.</Empty>
      ) : (
        <div className="ag-list">
          {list.map((b) => (
            <div key={b.id} className="ag-row" style={{ cursor: 'default' }}>
              <span className="ag-title">
                <b>{slotLine(b)}</b> · {b.userName}
                {b.clientName && <span className="muted"> ({b.clientName})</span>}
                {b.note && <div className="muted small">{b.note}</div>}
              </span>
              <button type="button" className="btn ghost" onClick={() => void cancel(b)} title="Annulla la prenotazione (chi l’ha fatta riceve l’avviso)">
                <Icon name="trash" size={14} />
              </button>
            </div>
          ))}
        </div>
      )}
    </Panel>
  );
}

// ---------------------------------------------------------------------------

function Settings({ ctx }: { ctx: Ctx }) {
  const { api } = ctx;
  const [s, setS] = useState<BookingSettings | null>(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    api.get<BookingSettings>('/api/booking/settings').then(setS).catch((e) => setError((e as Error).message));
  }, [api]);

  if (!s) return <Panel title="Disponibilità">{error ? <ErrorBanner message={error} /> : <div className="muted small">Caricamento…</div>}</Panel>;

  const setRule = (idx: number, patch: Partial<AvailabilityRule>) =>
    setS({ ...s, rules: s.rules.map((r, i) => (i === idx ? { ...r, ...patch } : r)) });
  const addRule = (weekday: number) => setS({ ...s, rules: [...s.rules, { weekday, startMin: 540, endMin: 780 }] });
  const delRule = (idx: number) => setS({ ...s, rules: s.rules.filter((_, i) => i !== idx) });

  const save = async () => {
    setBusy(true);
    setSaved(false);
    try {
      setS(await api.put<BookingSettings>('/api/booking/settings', s));
      setSaved(true);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="Disponibilità"
      subtitle="Quando clienti e commerciali possono prenotare"
      right={
        <button type="button" className="btn dark" onClick={() => void save()} disabled={busy}>
          {busy ? 'Salvataggio…' : saved ? 'Salvato' : 'Salva'}
        </button>
      }
    >
      <ErrorBanner message={error} onClose={() => setError(null)} />
      <label className="ag-allday" style={{ marginBottom: 10 }} title="Se la togli, nessuno può prenotare">
        <input type="checkbox" checked={s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} /> Prenotazioni attive
      </label>
      <div className="fields-2">
        <Field label="Durata di un appuntamento">
          <select value={s.slotMin} onChange={(e) => setS({ ...s, slotMin: Number(e.target.value) })}>
            {[15, 20, 30, 45, 60, 90, 120].map((n) => (
              <option key={n} value={n}>
                {n} minuti
              </option>
            ))}
          </select>
        </Field>
        <Field label="Preavviso minimo (ore)">
          <input type="number" min={0} value={s.minNoticeHours} onChange={(e) => setS({ ...s, minNoticeHours: Math.max(0, Number(e.target.value) || 0) })} />
        </Field>
        <Field label="Prenotabile fino a (giorni)">
          <input type="number" min={1} max={180} value={s.horizonDays} onChange={(e) => setS({ ...s, horizonDays: Math.min(180, Math.max(1, Number(e.target.value) || 1)) })} />
        </Field>
      </div>

      <h3 style={{ margin: '18px 0 8px' }}>Fasce settimanali</h3>
      <p className="muted small">Gli orari in cui hai già un appuntamento in agenda non risultano prenotabili.</p>
      {WEEK.map((w) => {
        const rows = s.rules.map((r, i) => ({ r, i })).filter((x) => x.r.weekday === w.d);
        return (
          <div key={w.d} className="bk-day">
            <b className="bk-dayname">{w.label}</b>
            <div className="bk-ranges">
              {rows.length === 0 && <span className="muted small">Non disponibile</span>}
              {rows.map(({ r, i }) => (
                <div key={i} className="bk-range">
                  <TimeInput value={minuteToTime(r.startMin)} onChange={(v) => setRule(i, { startMin: timeToMinute(v) ?? r.startMin })} from={6} to={22} step={30} />
                  <span>–</span>
                  <TimeInput value={minuteToTime(r.endMin)} onChange={(v) => setRule(i, { endMin: timeToMinute(v) ?? r.endMin })} from={6} to={22} step={30} />
                  <button type="button" className="btn ghost" onClick={() => delRule(i)} title="Togli fascia">
                    <Icon name="x" size={13} />
                  </button>
                </div>
              ))}
            </div>
            <button type="button" className="btn ghost" onClick={() => addRule(w.d)} title="Aggiungi fascia">
              <Icon name="plus" size={14} />
            </button>
          </div>
        );
      })}
    </Panel>
  );
}
